'use client';

import { useState } from 'react';
import { ChevronRight, Minus, Plus } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { minimumStepQuantity } from '@/lib/chat/line-editor-block';
import { chaseRequest } from '@/lib/chat/owed-table-block';
import { uiBlockKey, type LineEditorBlock, type OwedTableBlock, type UiBlock, type WeekTimelineBlock } from '@/lib/chat/ui-blocks';
import { ApprovalStack } from '@/components/chat/blocks/ApprovalStack';
import { DocumentCard } from '@/components/chat/blocks/DocumentCard';
import { RevenueChart } from '@/components/chat/blocks/RevenueChart';
import { ClientChoices } from '@/components/chat/blocks/ClientChoices';
import { JobBrief } from '@/components/chat/blocks/JobBrief';
import { QuoteHistory } from '@/components/chat/blocks/QuoteHistory';
import { PriceRange } from '@/components/chat/blocks/PriceRange';
import { CertsDue } from '@/components/chat/blocks/CertsDue';
import { PhotoPicker } from '@/components/chat/blocks/PhotoPicker';
import { AvailabilityStrip } from '@/components/chat/blocks/AvailabilityStrip';
import { SlotPicker } from '@/components/chat/blocks/SlotPicker';
import { TeamMap } from '@/components/chat/blocks/TeamMap';
import { CallCards } from '@/components/chat/blocks/CallCards';
import { PartsChecklist } from '@/components/chat/blocks/PartsChecklist';
import { ReceiptLines } from '@/components/chat/blocks/ReceiptLines';
import { MorningBriefing } from '@/components/chat/blocks/MorningBriefing';
import { SupplierOrder } from '@/components/chat/blocks/SupplierOrder';

/**
 * Server-built components inside a chat bubble (`ui-blocks.ts`).
 *
 * One file for both surfaces, the same as `BubbleBlock.tsx` and for the same
 * reason: MuckaPanel and the Mucka Bar drifted every time they were two. Each
 * block is a strip inside her bubble, after her words and before any rows or
 * actions, divided off by the bubble's own hairline.
 *
 * 🚨 Nothing here computes a figure. Every number comes off the block, and the
 * block comes off the server's own read of the quote, so the bubble and the PDF
 * cannot disagree. While a step is saving the new quantity shows at once and
 * the money waits for the server.
 */

/** A line editor's save state, owned by MuckaPanel and published to the bar. */
export interface LineEditorState {
  saving: boolean;
  /** The server's own sentence when a save was refused (#35, inline per #42). */
  error: string | null;
  /** True once a step in this conversation has saved. */
  saved: boolean;
  /** Quantities asked for and not yet saved, by line id. */
  pending: Record<string, number>;
}

export const IDLE_EDITOR: LineEditorState = { saving: false, error: null, saved: false, pending: {} };

function pounds(n: number): string {
  // Signed: a discount line is a minus price, and "£-224.00" puts the sign on the wrong side.
  return `${n < 0 ? '-' : ''}£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function quantityLabel(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

function LineEditor({ block, state, canEdit, onStep }: {
  block: LineEditorBlock;
  state: LineEditorState;
  canEdit: boolean;
  onStep: (quoteId: string, lineId: string, quantity: number) => void;
}) {
  const editable = canEdit && block.status === 'draft';
  const settling = state.saving && Object.keys(state.pending).length > 0;
  const status = state.error
    ? null
    : state.saving
      ? 'Saving…'
      : state.saved
        ? 'Saved to the draft · nothing has gone to the customer'
        : block.status === 'draft'
          ? 'Draft · nothing has gone to the customer'
          : null;

  return (
    <div className="mchat-editor" role="group" aria-label={`${block.reference}, ${block.lines.length} lines`}>
      {block.lines.map(line => {
        const pending = state.pending[line.id];
        const quantity = pending ?? line.quantity;
        const canStep = editable && line.steppable;
        return (
          <div className="mchat-editor-line" key={line.id}>
            <span className="mchat-editor-main">
              <span className="mchat-editor-desc">{line.description}</span>
              <span className="mchat-editor-unit">{pounds(line.unitPrice)} each</span>
            </span>
            {canStep ? (
              <span className="mchat-stepper">
                <button
                  type="button"
                  className="mchat-stepper-btn"
                  aria-label={`One fewer: ${line.description}`}
                  disabled={quantity <= minimumStepQuantity(line.quantity)}
                  onClick={() => onStep(block.quoteId, line.id, quantity - 1)}
                >
                  <Icon icon={Minus} size={15} />
                </button>
                <span className="mchat-stepper-qty" aria-live="polite">{quantityLabel(quantity)}</span>
                <button
                  type="button"
                  className="mchat-stepper-btn"
                  aria-label={`One more: ${line.description}`}
                  onClick={() => onStep(block.quoteId, line.id, quantity + 1)}
                >
                  <Icon icon={Plus} size={15} />
                </button>
              </span>
            ) : (
              <span className="mchat-editor-qty">× {quantityLabel(quantity)}</span>
            )}
            <span className={`mchat-editor-amount${pending !== undefined ? ' mchat-editor-amount--settling' : ''}`}>
              {pounds(line.lineTotal)}
            </span>
          </div>
        );
      })}
      <div className={`mchat-editor-totals${settling ? ' mchat-editor-totals--settling' : ''}`}>
        <span className="mchat-editor-sum"><span>Subtotal</span><span>{pounds(block.subtotal)}</span></span>
        <span className="mchat-editor-sum"><span>VAT {quantityLabel(block.taxRate)}%</span><span>{pounds(block.taxAmount)}</span></span>
        <span className="mchat-editor-sum mchat-editor-sum--total"><span>Total</span><span>{pounds(block.total)}</span></span>
      </div>
      {state.error && <p className="mchat-body mchat-error" role="alert">{state.error}</p>}
      {status && <p className="mchat-editor-status" aria-live="polite">{status}</p>}
    </div>
  );
}

/** "12 days", "1 day", or plain "Overdue" when there is no due date to count from. */
function overdueFor(days: number | null): string {
  if (days == null) return 'Overdue';
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * Who owes you. A row opens the invoice; Chase ASKS Mucka, in words, to chase
 * it — the tap sends nothing to the customer. What goes out is then prepared
 * by the ordinary chase path, with its dispute and Breathing Space checks, and
 * approved on its own card. One send path, so nothing here can skip a check.
 */
function OwedTable({ block, onAnswer, onOpen, live }: {
  block: OwedTableBlock;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
  live: boolean;
}) {
  const [asked, setAsked] = useState<Record<string, boolean>>({});
  return (
    <div className="mchat-owed" role="list" aria-label={`${block.rows.length + block.more} overdue invoices`}>
      {block.rows.map(row => (
        <div className="mchat-owed-row" role="listitem" key={row.invoiceId}>
          <button type="button" className="mchat-owed-open" onClick={() => onOpen(row.path)}>
            <span className="mchat-owed-main">
              <span className="mchat-owed-who">{row.who}</span>
              <span className="mchat-owed-meta">
                {row.reference}
                <span className={`mchat-days${row.daysOverdue != null && row.daysOverdue >= 30 ? ' mchat-days--late' : ''}`}>{overdueFor(row.daysOverdue)}</span>
              </span>
            </span>
            <span className="mchat-owed-amount">{pounds(row.amount)}</span>
          </button>
          {row.chaseable && (
            <button
              type="button"
              className="mchat-chase"
              disabled={!live || asked[row.invoiceId]}
              aria-label={`Chase ${row.reference}, ${row.who}`}
              onClick={() => { setAsked(prev => ({ ...prev, [row.invoiceId]: true })); onAnswer(chaseRequest(row)); }}
            >
              {asked[row.invoiceId] ? 'Asked' : 'Chase'}
            </button>
          )}
        </div>
      ))}
      <div className="mchat-owed-total"><span>Owed{block.more ? `, including ${block.more} more not shown` : ''}</span><span>{pounds(block.totalOwed)}</span></div>
    </div>
  );
}

/** A week of jobs, one day at a time. Every stop opens its job. */
function WeekTimeline({ block, onOpen }: { block: WeekTimelineBlock; onOpen: (path: string) => void }) {
  const [day, setDay] = useState(block.focusDate);
  const shown = block.days.find(d => d.date === day) ?? block.days[0];
  return (
    <div className="mchat-week">
      <div className="mchat-week-days" role="tablist" aria-label="Day">
        {block.days.map(d => (
          <button
            key={d.date}
            type="button"
            role="tab"
            aria-selected={d.date === shown.date}
            className={`mchat-week-day${d.date === shown.date ? ' mchat-week-day--on' : ''}`}
            onClick={() => setDay(d.date)}
          >
            <span className="mchat-week-day-name">{d.label}</span>
            <span className="mchat-week-day-count">{d.stops.length}</span>
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={shown.label}>
        {shown.stops.map(stop => (
          <button key={stop.jobId} type="button" className="mchat-row mchat-week-stop" onClick={() => onOpen(stop.path)}>
            <span className="mchat-week-time">{stop.time ?? '—'}</span>
            <span className="mchat-row-main">
              <span className="mchat-row-title">
                {stop.title}
                {stop.pencilled && <span className="mchat-pencilled">Pencilled</span>}
              </span>
              <span className="mchat-row-detail">{[stop.where, stop.who].filter(Boolean).join(' · ')}</span>
            </span>
            <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
          </button>
        ))}
      </div>
    </div>
  );
}

export interface UiBlockHandlers {
  onLineStep: (quoteId: string, lineId: string, quantity: number) => void;
  /** Send this text to Mucka as the owner's own message. */
  onAnswer: (text: string) => void;
  /** Go to an app path. */
  onOpen: (path: string) => void;
}

export function UiBlockView({ block, editorState, canEditQuotes, live = true, onLineStep, onAnswer, onOpen }: {
  block: UiBlock;
  editorState?: LineEditorState;
  canEditQuotes: boolean;
  /** False on an older reply: its questions are about state that has moved on. */
  live?: boolean;
} & UiBlockHandlers) {
  switch (block.kind) {
    case 'line-editor':
      return <LineEditor block={block} state={editorState ?? IDLE_EDITOR} canEdit={canEditQuotes} onStep={onLineStep} />;
    case 'owed-table':
      return <OwedTable block={block} onAnswer={onAnswer} onOpen={onOpen} live={live} />;
    case 'week-timeline':
      return <WeekTimeline block={block} onOpen={onOpen} />;
    case 'approval-stack':
      return <ApprovalStack block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'document-card':
      return <DocumentCard block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'revenue-chart':
      return <RevenueChart block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'client-choices':
      return <ClientChoices block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'job-brief':
      return <JobBrief block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'quote-history':
      return <QuoteHistory block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'price-range':
      return <PriceRange block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'certs-due':
      return <CertsDue block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'photo-picker':
      return <PhotoPicker block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'availability-strip':
      return <AvailabilityStrip block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'slot-picker':
      return <SlotPicker block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'team-map':
      return <TeamMap block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'call-cards':
      return <CallCards block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'parts-checklist':
      return <PartsChecklist block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'receipt-lines':
      return <ReceiptLines block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'morning-briefing':
      return <MorningBriefing block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    case 'supplier-order':
      return <SupplierOrder block={block} live={live} onAnswer={onAnswer} onOpen={onOpen} />;
    default:
      // A kind this build knows the type of but does not draw yet. The
      // sentence above it already carries the answer.
      return null;
  }
}

/** True when a message's own rows are shown as a component instead (week-timeline, team-map). */
export function blocksReplaceJobRows(blocks: readonly UiBlock[] | undefined): boolean {
  // The team map draws the same day's jobs a person at a time, so the rows
  // would be the day twice.
  return !!blocks?.some(b => b.kind === 'week-timeline' || b.kind === 'team-map');
}

/** All of a message's blocks, in order. */
export function UiBlocks({ blocks, editorStates, canEditQuotes, live, onLineStep, onAnswer, onOpen }: {
  blocks: readonly UiBlock[] | undefined;
  editorStates: Record<string, LineEditorState> | undefined;
  canEditQuotes: boolean;
  live?: boolean;
} & UiBlockHandlers) {
  if (!blocks?.length) return null;
  return (
    <>
      {blocks.map(block => (
        <UiBlockView
          key={uiBlockKey(block)}
          block={block}
          editorState={block.kind === 'line-editor' ? editorStates?.[block.quoteId] : undefined}
          canEditQuotes={canEditQuotes}
          live={live}
          onLineStep={onLineStep}
          onAnswer={onAnswer}
          onOpen={onOpen}
        />
      ))}
    </>
  );
}
